export const LEGACY_KEYS = {
  ACTIVESTEP: 'activestep',
  IND: 'ind',
} as const;

export const CANONICAL_KEYS = {
  ACTIVE_STEP: 'activeStep',
  STEP: 'step',
  ID: 'id',
  USECASE_ID: 'usecase_id',
  LEVEL2_ID: 'level2_id',
} as const;

export const STEP_VALUES = {
  IDEATION_INTAKE: 'ideationIntake',
  PROPOSED_ENHANCEMENT: 'proposedEnhancement',
  PROCESS_DIAGRAM: 'processDiagram',
} as const;

const LEGACY_STEP_VALUE_MAP: Record<string, string> = {
  'ideationintake': STEP_VALUES.IDEATION_INTAKE,
  'proposedenhancement': STEP_VALUES.PROPOSED_ENHANCEMENT,
  'processdiagram': STEP_VALUES.PROCESS_DIAGRAM,
};

const IND_VALUE_MAP: Record<string, string> = {
  'Ideation Intake': STEP_VALUES.IDEATION_INTAKE,
  'Proposed Enhancement': STEP_VALUES.PROPOSED_ENHANCEMENT,
  'Process Diagram': STEP_VALUES.PROCESS_DIAGRAM,
  'proposedenhancement': STEP_VALUES.PROPOSED_ENHANCEMENT,
};

/**
 * Normalizes search parameters to canonical format.
 * Maps legacy keys and values to their canonical counterparts.
 * Removes redundant/deprecated parameters like 'ind'.
 */
export const normalizeSearchParams = (params: URLSearchParams): {
  normalizedParams: URLSearchParams;
  hasChanges: boolean;
} => {
  const newParams = new URLSearchParams(params);
  let hasChanges = false;

  // 1. Handle 'activestep' -> 'activeStep'
  if (newParams.has(LEGACY_KEYS.ACTIVESTEP)) {
    const value = newParams.get(LEGACY_KEYS.ACTIVESTEP);
    if (value !== null) {
      newParams.set(CANONICAL_KEYS.ACTIVE_STEP, value);
      newParams.delete(LEGACY_KEYS.ACTIVESTEP);
      hasChanges = true;
    }
  }

  // 2. Handle 'ind' -> remove and potentially update 'step' if 'step' is missing
  if (newParams.has(LEGACY_KEYS.IND)) {
    const indValue = newParams.get(LEGACY_KEYS.IND);
    if (indValue && !newParams.has(CANONICAL_KEYS.STEP)) {
      const mappedStep = IND_VALUE_MAP[indValue] || IND_VALUE_MAP[decodeURIComponent(indValue)];
      if (mappedStep) {
        newParams.set(CANONICAL_KEYS.STEP, mappedStep);
      }
    }
    newParams.delete(LEGACY_KEYS.IND);
    hasChanges = true;
  }

  // 3. Normalize 'step' value casing
  if (newParams.has(CANONICAL_KEYS.STEP)) {
    const stepValue = newParams.get(CANONICAL_KEYS.STEP);
    if (stepValue) {
      const lowerStep = stepValue.toLowerCase();
      if (LEGACY_STEP_VALUE_MAP[lowerStep] && LEGACY_STEP_VALUE_MAP[lowerStep] !== stepValue) {
        newParams.set(CANONICAL_KEYS.STEP, LEGACY_STEP_VALUE_MAP[lowerStep]);
        hasChanges = true;
      }
    }
  }

  // 4. Handle 'usecase_id' -> map to 'id' if 'id' is missing (Internal logic uses 'id')
  // However, user wants to SHOW 'usecase_id' in URL for some steps.
  // Special Rule: If step is intake/enhancement, ensure we have 'usecase_id'.
  // If step is processDiagram, ensure we have 'id'.
  const currentStep = newParams.get(CANONICAL_KEYS.STEP);
  if (currentStep === STEP_VALUES.IDEATION_INTAKE || currentStep === STEP_VALUES.PROPOSED_ENHANCEMENT) {
    if (newParams.has(CANONICAL_KEYS.ID)) {
      const idVal = newParams.get(CANONICAL_KEYS.ID);
      newParams.set(CANONICAL_KEYS.USECASE_ID, idVal!);
      newParams.delete(CANONICAL_KEYS.ID);
      hasChanges = true;
    }
  } else if (currentStep === STEP_VALUES.PROCESS_DIAGRAM) {
    // For process diagram, we allow both level2_id and usecase_id to coexist.
    // If only 'id' is present, it's typically a level2_id.
    if (newParams.has(CANONICAL_KEYS.ID) && !newParams.has(CANONICAL_KEYS.LEVEL2_ID)) {
      const idVal = newParams.get(CANONICAL_KEYS.ID);
      newParams.set(CANONICAL_KEYS.LEVEL2_ID, idVal!);
      newParams.delete(CANONICAL_KEYS.ID);
      hasChanges = true;
    }
  }

  return { normalizedParams: newParams, hasChanges };
};

/**
 * Generates a canonical URL search string for navigation.
 */
export const getCanonicalUrl = (params: {
  step?: string;
  activeStep?: number | string;
  id?: string;
  usecaseId?: string;
}): string => {
  const searchParams = new URLSearchParams();

  let normalizedStep = '';
  if (params.step) {
    normalizedStep = LEGACY_STEP_VALUE_MAP[params.step.toLowerCase()] || params.step;
    searchParams.set(CANONICAL_KEYS.STEP, normalizedStep);
  }

  if (params.activeStep !== undefined) {
    searchParams.set(CANONICAL_KEYS.ACTIVE_STEP, String(params.activeStep));
  }

  if (params.id) {
    // If step is intake or enhancement, use 'usecase_id' key
    if (normalizedStep === STEP_VALUES.IDEATION_INTAKE || normalizedStep === STEP_VALUES.PROPOSED_ENHANCEMENT) {
      searchParams.set(CANONICAL_KEYS.USECASE_ID, params.id);
    } else {
      searchParams.set(CANONICAL_KEYS.LEVEL2_ID, params.id);
    }
  }

  if (params.usecaseId) {
    searchParams.set(CANONICAL_KEYS.USECASE_ID, params.usecaseId);
  }

  const query = searchParams.toString();
  return query ? `?${query}` : '';
};
